import React from "react";

const DEFAULT_COLOUR = "#14704C";
// Dark masjid colours (navy, maroon, black) are lightened until they show on a dark screen.
const MIN_LUMINANCE = 0.1;

const parseHex = (hex?: string | null): number[] | null => {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  return m ? [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16)) : null;
};

const luminance = (rgb: number[]): number => {
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const toHex = (rgb: number[]): string =>
  `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;

const inkFor = (rgb: number[]): string =>
  luminance(rgb) > 0.179 ? "#111111" : "#FFFFFF";

// The masjid's colour as CSS variables for layouts to use: --masjid-colour, and
// --masjid-colour-ink for text drawn on top of it (black or white, whichever reads better).
export function masjidColourVars(hex?: string | null): React.CSSProperties {
  let rgb = parseHex(hex) ?? parseHex(DEFAULT_COLOUR)!;
  while (luminance(rgb) < MIN_LUMINANCE) {
    rgb = rgb.map((c) => c + (255 - c) * 0.1);
  }
  return {
    "--masjid-colour": toHex(rgb),
    "--masjid-colour-ink": inkFor(rgb),
  } as React.CSSProperties;
}

// The masjid's colour exactly as set, for solid bands on a light page (e.g. the monthly
// timetable's header), with the text colour that reads on it.
export function masjidSolidColour(hex?: string | null): { colour: string; ink: string } {
  const rgb = parseHex(hex) ?? parseHex(DEFAULT_COLOUR)!;
  return { colour: toHex(rgb), ink: inkFor(rgb) };
}
